/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { addMonths, format, isSameDay, lastDayOfMonth, startOfMonth } from 'date-fns';
import {
  BOOKING_DATE_START_DATE,
  MONTH_NAME_DAY_YEAR_PATTERN,
  YEAR_MONTH_DAY_PATTERN,
} from 'modules/common/constants/date-range';
import { ENVIRONMENT } from 'config';
import logger from 'modules/common/utils/logger';
import calculateComparisonDates from 'modules/common/utils/dateComparisonUtils';
import { VISUAL_DATE_TYPES } from 'modules/dashboard/constants';
import { compareWindows } from 'modules/common/ui-controller';
import { formatDate } from 'modules/dashboard/functions';
import {
  ANONYMIZED,
  ARRIVAL_DATE_RANGE,
  ARRIVAL_STAY_DATE_SWITCH_FILTER,
  BOOKING_DATE_COMPARISON,
  BOOKING_DATE_FILTER_LOWER,
  BOOKING_DATE_FILTER_UPPER,
  BOOKING_DATE_LOWER_FILTER,
  BOOKING_DATE_RANGE,
  BOOKING_DATE_UPPER_FILTER,
  COLUMN_DYNAMIC_MEASURE,
  COLUMN_DYNAMIC_MEASURE_ST,
  COMPARISON_BOOKING_DATE_FILTER_LOWER,
  COMPARISON_BOOKING_DATE_FILTER_UPPER,
  COMPARISON_BOOKING_OTB_LOWER,
  COMPARISON_BOOKING_OTB_UPPER,
  CURRENCY_COMPARISON,
  CURRENCY_FILTER,
  CURRENT_YEAR_FILTER,
  DYNAMIC_COMPARISON_TIME_FILTER_STAY_DATE_LOWER,
  DYNAMIC_COMPARISON_TIME_FILTER_STAY_DATE_UPPER,
  DYNAMIC_STAY_DATE_FILTER_LOWER,
  DYNAMIC_STAY_DATE_FILTER_UPPER,
  HOTEL_FILTER,
  IS_PREDEFINED,
  LAST_YEAR_FILTER,
  PACE_TABULAR_TITLE,
  PACE_VIEW_RANGE_FILTER,
  PACE_VIEW_RANGE_FILTER_VALUE,
  PMS_FILTER,
  PMS_SYNC,
  SELECTED_CURRENCY,
  SELECTED_DATE_FILTER,
  SET_NAME,
  STAY_DATE_COMPARISON,
  STAY_DATE_LOWER_FILTER,
  STAY_DATE_RANGE,
  STAY_DATE_UPPER_FILTER,
  TABLE_MEASURES,
  YEAR_BEFORE_LAST_YEAR_FILTER,
} from './constants';

// Define a function getResult using arrow function syntax
const getMapperResult = (alignment, dateComparison) => {
  // Define a mapping object that maps alignment states and dateComparison values to index numbers
  const mapping = {
    true: {
      'vs OTB': 1,
      'vs Previous Week (OTB)': 2,
      'vs Previous Month (OTB)': 5,
      'vs Previous Year (OTB)': 6,
      Custom: 7, // Add Custom index for alignment true
    },
    false: {
      'vs OTB': 1,
      'vs Previous Week (OTB)': 2,
      'vs Previous Month (OTB)': 3,
      'vs Previous Year (OTB)': 4,
      Custom: 7, // Add Custom index for alignment false
    },
  };

  // Select the appropriate mapping based on the alignment
  const alignmentMapping = alignment ? mapping.true : mapping.false;

  // Return the index number corresponding to the dateComparison value
  return alignmentMapping[dateComparison];
};
//
export default getMapperResult;

/**
 * Creates a Cerulean date filter object for query use.
 *
 * @param {string} filterName - The name of the filter (used as a key in the final object).
 * @param {string | Date} date - The date value to filter by.
 * @param {string} [table=TABLE_MEASURES] - The table name for the target (default is TABLE_MEASURES).
 * @param {string} [columns=COLUMN_DYNAMIC_MEASURE] - The column name for the target (default is COLUMN_DYNAMIC_MEASURE).
 * @param {string} [operator='In'] - The filter operator to use (default is 'In').
 * @returns {Object} A Cerulean date filter object formatted for query use.
 */
export const createCeruleanDateFilter = (
  filterName,
  date,
  table = TABLE_MEASURES,
  columns = COLUMN_DYNAMIC_MEASURE,
  operator = 'In'
) => ({
  [filterName]: {
    target: {
      table,
      columns,
    },
    conditions: {
      operator,
      values: `${format(new Date(date), YEAR_MONTH_DAY_PATTERN)}T00:00:00.000`,
    },
  },
});

/**
 * Creates a Cerulean filter object for query use.
 *
 * @param {string} filterName - The name of the filter (used as a key in the final object).
 * @param {any} values - An any of values to filter by.
 * @param {string} [table=TABLE_MEASURES] - The table name for the target (default is TABLE_MEASURES).
 * @param {string} [columns=COLUMN_DYNAMIC_MEASURE] - The column name for the target (default is COLUMN_DYNAMIC_MEASURE).
 * @param {string} [operator='In'] - The filter operator to use (default is 'In').
 * @returns {Object} A Cerulean filter object formatted for query use.
 */
export const createCeruleanFilter = (
  filterName,
  values,
  table = TABLE_MEASURES,
  columns = COLUMN_DYNAMIC_MEASURE,
  operator = 'In'
) => ({
  [filterName]: {
    target: {
      table,
      columns,
    },
    conditions: {
      operator,
      values,
    },
  },
});

export const buildPaceFilters = ({ bookingDate, targetDate, arrival, pms, hotels }) => {
  const hotelIds = !hotels?.hotels
    ? [hotels?.hotelCode]
    : hotels?.hotels?.map((hotel) => hotel?.hotelCode);
  return {
    ...createCeruleanDateFilter(STAY_DATE_LOWER_FILTER, targetDate.startDate),
    ...createCeruleanDateFilter(STAY_DATE_UPPER_FILTER, targetDate.endDate),
    ...createCeruleanFilter(ARRIVAL_STAY_DATE_SWITCH_FILTER, arrival),
    ...createCeruleanDateFilter(BOOKING_DATE_LOWER_FILTER, bookingDate.startDate),
    ...createCeruleanDateFilter(BOOKING_DATE_UPPER_FILTER, bookingDate.endDate),
    ...createCeruleanFilter(PMS_FILTER, pms?.toString()),
    ...createCeruleanFilter(HOTEL_FILTER, hotelIds),
    ...createCeruleanFilter(PACE_VIEW_RANGE_FILTER, PACE_VIEW_RANGE_FILTER_VALUE),
  };
};

const getDateComparison = (dateComparison, aligned) => {
  let comparisonLabel;
  if (dateComparison === compareWindows.otb) {
    comparisonLabel = 'OTB';
  } else if (dateComparison === compareWindows.cus) {
    comparisonLabel = 'Custom';
  } else if (aligned) {
    const labelMap = {
      [compareWindows.py]: 'Previous Year - Aligned',
      [compareWindows.pm]: 'Previous Month - Aligned',
      [compareWindows.pw]: 'Previous Week - Aligned',
    };
    comparisonLabel = labelMap[dateComparison];
  } else {
    const labelMap = {
      [compareWindows.py]: 'Previous Year - Unaligned',
      [compareWindows.pm]: 'Previous Month - Unaligned',
      [compareWindows.pw]: 'Previous Week - Unaligned',
    };
    comparisonLabel = labelMap[dateComparison];
  }
  return comparisonLabel;
};

export const handleCeruleanLogging = ({
  message,
  level = 'info',
  sessionId,
  user,
  eventType = null,
}) => {
  if (ENVIRONMENT.CERULEAN_TABULAR_DEBUG_MODE?.toLowerCase() === 'true') {
    // eslint-disable-next-line no-console
    console.log(message);
  }
  //
  if (user?.username) {
    logger.setUser({ username: user.username });
  }
  logger.logEvent({
    name: 'cerulean_tabular_log',
    level,
    context: {
      sessionId,
      eventType,
      message,
    },
  });
};

export const updateMuiPaceColumnLabels = (columns, columnData) =>
  columns.map((col) => {
    switch (col.id) {
      case 'this_year_rooms':
        return {
          ...col,
          label: `This Year as of ${
            format(new Date(columnData?.month), MONTH_NAME_DAY_YEAR_PATTERN) ?? ''
          }`,
        };
      case 'last_year_rooms':
        return {
          ...col,
          label: `Last Year as of ${
            format(new Date(columnData?.channel), MONTH_NAME_DAY_YEAR_PATTERN) ?? ''
          }`,
        };
      default:
        return col;
    }
  });

export const buildRatePlanFilters = ({
  bookingDate,
  targetDate,
  pms,
  hotels,
  currencyType,
  alignmentToggle,
  symmetricComparisonToggle,
  dateComparison,
  startDate,
  endDate,
  resetBookingDate,
  partialHours,
  lastBookingDate,
}) => {
  const { adjustedStayDate, adjustedBookingDate } = calculateComparisonDates(
    targetDate,
    bookingDate,
    alignmentToggle,
    symmetricComparisonToggle,
    dateComparison,
    { startDate, endDate },
    resetBookingDate,
    partialHours,
    lastBookingDate
  );
  const bookingOtb = {
    startDate: new Date(BOOKING_DATE_START_DATE),
    endDate: new Date(),
  };
  const { adjustedBookingDate: adjustedBookingOtbDate } = calculateComparisonDates(
    targetDate,
    bookingOtb,
    alignmentToggle,
    symmetricComparisonToggle,
    dateComparison,
    { startDate, endDate },
    resetBookingDate,
    partialHours,
    lastBookingDate
  );
  const hotelIds = !hotels?.hotels
    ? [hotels?.hotelCode]
    : hotels?.hotels?.map((hotel) => hotel?.hotelCode);
  return {
    // stay date filters
    ...createCeruleanDateFilter(DYNAMIC_STAY_DATE_FILTER_LOWER, targetDate.startDate),
    ...createCeruleanDateFilter(DYNAMIC_STAY_DATE_FILTER_UPPER, targetDate.endDate),

    // comparison stay date filters
    ...createCeruleanDateFilter(
      DYNAMIC_COMPARISON_TIME_FILTER_STAY_DATE_LOWER,
      adjustedStayDate?.startDate
    ),
    ...createCeruleanDateFilter(
      DYNAMIC_COMPARISON_TIME_FILTER_STAY_DATE_UPPER,
      adjustedStayDate?.endDate
    ),

    // booking date filters
    ...createCeruleanDateFilter(BOOKING_DATE_FILTER_LOWER, bookingDate.startDate),
    ...createCeruleanDateFilter(BOOKING_DATE_FILTER_UPPER, bookingDate.endDate),

    // comparison booking date filters
    ...createCeruleanDateFilter(
      COMPARISON_BOOKING_DATE_FILTER_LOWER,
      adjustedBookingDate?.startDate
    ),
    ...createCeruleanDateFilter(COMPARISON_BOOKING_DATE_FILTER_UPPER, adjustedBookingDate?.endDate),

    // comparison booking OTB filters
    ...createCeruleanDateFilter(COMPARISON_BOOKING_OTB_LOWER, adjustedBookingOtbDate?.startDate),
    ...createCeruleanDateFilter(COMPARISON_BOOKING_OTB_UPPER, adjustedBookingOtbDate?.endDate),

    // PMS filter
    ...createCeruleanFilter(PMS_FILTER, pms?.toString()),

    // hotel filter
    ...createCeruleanFilter(HOTEL_FILTER, hotelIds),

    // currency filter
    ...createCeruleanFilter(CURRENCY_FILTER, currencyType),
  };
};

export const buildRoomTypeHeaderData = ({
  targetDate,
  bookingDate,
  alignmentToggle,
  symmetricComparisonToggle,
  currencyType,
  aligned = false,
  selectedDateType,
  dateComparison,
  startDate,
  endDate,
  resetBookingDate,
  partialHours,
  lastBookingDate,
}) => {
  const isOtb =
    isSameDay(new Date(bookingDate?.startDate), new Date(resetBookingDate?.startDate)) &&
    isSameDay(new Date(bookingDate?.endDate), new Date(resetBookingDate?.endDate));

  const { adjustedStayDate, adjustedBookingDate } = calculateComparisonDates(
    targetDate,
    bookingDate,
    alignmentToggle,
    symmetricComparisonToggle,
    dateComparison,
    { startDate, endDate },
    resetBookingDate,
    partialHours,
    lastBookingDate
  );
  //
  const stayDateRange = `${format(
    new Date(targetDate.startDate),
    YEAR_MONTH_DAY_PATTERN
  )} to ${format(new Date(targetDate.endDate), YEAR_MONTH_DAY_PATTERN)}`;

  const stayDateComparison = `${format(
    new Date(adjustedStayDate.startDate),
    YEAR_MONTH_DAY_PATTERN
  )} to ${format(new Date(adjustedStayDate.endDate), YEAR_MONTH_DAY_PATTERN)}`;
  //
  const bookingDateRange = `${format(
    new Date(bookingDate.startDate),
    YEAR_MONTH_DAY_PATTERN
  )} to ${format(new Date(bookingDate.endDate), YEAR_MONTH_DAY_PATTERN)}`;
  const bookingDateComparison = `${format(
    new Date(adjustedBookingDate.startDate),
    YEAR_MONTH_DAY_PATTERN
  )} to ${format(new Date(adjustedBookingDate.endDate), YEAR_MONTH_DAY_PATTERN)}`;
  //
  let comparisonLabel;

  if (dateComparison === compareWindows.otb) {
    comparisonLabel = 'OTB';
  } else if (dateComparison === compareWindows.cus) {
    comparisonLabel = 'Custom';
  } else if (aligned) {
    const labelMap = {
      [compareWindows.py]: 'Previous Year - Aligned',
      [compareWindows.pm]: 'Previous Month - Aligned',
      [compareWindows.pw]: 'Previous Week - Aligned',
    };
    comparisonLabel = labelMap[dateComparison];
  } else {
    const labelMap = {
      [compareWindows.py]: 'Previous Year - Unaligned',
      [compareWindows.pm]: 'Previous Month - Unaligned',
      [compareWindows.pw]: 'Previous Week - Unaligned',
    };
    comparisonLabel = labelMap[dateComparison];
  }

  return [
    {
      id: 1,
      title: selectedDateType === 'ARRIVAL' ? ARRIVAL_DATE_RANGE : STAY_DATE_RANGE,
      value: stayDateRange,
    },
    { id: 2, title: BOOKING_DATE_RANGE, value: isOtb ? 'OTB' : bookingDateRange },
    { title: SELECTED_CURRENCY, value: currencyType },
    { id: 3, title: STAY_DATE_COMPARISON, value: stayDateComparison },
    {
      id: 4,
      title: BOOKING_DATE_COMPARISON,
      value: `${bookingDateComparison} ${symmetricComparisonToggle ? ' (STLP)' : ''}`,
    },
    { id: 5, title: CURRENCY_COMPARISON, value: comparisonLabel },
  ];
};

export const buildRatePlanHeaderData = ({
  targetDate,
  bookingDate,
  alignmentToggle,
  symmetricComparisonToggle,
  currencyType,
  aligned = false,
  selectedDateType,
  dateComparison,
  startDate,
  endDate,
  resetBookingDate,
  partialHours,
  lastBookingDate,
}) => {
  const isOtb =
    isSameDay(new Date(bookingDate?.startDate), new Date(resetBookingDate?.startDate)) &&
    isSameDay(new Date(bookingDate?.endDate), new Date(resetBookingDate?.endDate));

  const { adjustedStayDate, adjustedBookingDate } = calculateComparisonDates(
    targetDate,
    bookingDate,
    alignmentToggle,
    symmetricComparisonToggle,
    dateComparison,
    { startDate, endDate },
    resetBookingDate,
    partialHours,
    lastBookingDate
  );
  //
  const stayDateRange = `${format(
    new Date(targetDate.startDate),
    YEAR_MONTH_DAY_PATTERN
  )} to ${format(new Date(targetDate.endDate), YEAR_MONTH_DAY_PATTERN)}`;

  const stayDateComparison = `${format(
    new Date(adjustedStayDate.startDate),
    YEAR_MONTH_DAY_PATTERN
  )} to ${format(new Date(adjustedStayDate.endDate), YEAR_MONTH_DAY_PATTERN)}`;
  //
  const bookingDateRange = `${format(
    new Date(bookingDate.startDate),
    YEAR_MONTH_DAY_PATTERN
  )} to ${format(new Date(bookingDate.endDate), YEAR_MONTH_DAY_PATTERN)}`;
  const bookingDateComparison = `${format(
    new Date(adjustedBookingDate.startDate),
    YEAR_MONTH_DAY_PATTERN
  )} to ${format(new Date(adjustedBookingDate.endDate), YEAR_MONTH_DAY_PATTERN)}`;
  //
  let comparisonLabel;

  if (dateComparison === compareWindows.otb) {
    comparisonLabel = 'OTB';
  } else if (dateComparison === compareWindows.cus) {
    comparisonLabel = 'Custom';
  } else if (aligned) {
    const labelMap = {
      [compareWindows.py]: 'Previous Year - Aligned',
      [compareWindows.pm]: 'Previous Month - Aligned',
      [compareWindows.pw]: 'Previous Week - Aligned',
    };
    comparisonLabel = labelMap[dateComparison];
  } else {
    const labelMap = {
      [compareWindows.py]: 'Previous Year - Unaligned',
      [compareWindows.pm]: 'Previous Month - Unaligned',
      [compareWindows.pw]: 'Previous Week - Unaligned',
    };
    comparisonLabel = labelMap[dateComparison];
  }

  return [
    {
      id: 1,
      title: selectedDateType === 'ARRIVAL' ? ARRIVAL_DATE_RANGE : STAY_DATE_RANGE,
      value: stayDateRange,
    },
    { id: 2, title: BOOKING_DATE_RANGE, value: isOtb ? 'OTB' : bookingDateRange },
    { title: SELECTED_CURRENCY, value: currencyType },
    { id: 3, title: STAY_DATE_COMPARISON, value: stayDateComparison },
    {
      id: 4,
      title: BOOKING_DATE_COMPARISON,
      value: `${bookingDateComparison} ${symmetricComparisonToggle ? ' (STLP)' : ''}`,
    },
    { id: 5, title: CURRENCY_COMPARISON, value: comparisonLabel },
  ];
};

export const buildRateCategoryFilters = ({
  bookingDate,
  targetDate,
  pms,
  hotels,
  currencyType,
  alignmentToggle,
  symmetricComparisonToggle,
  dateComparison,
  startDate,
  endDate,
  resetBookingDate,
  arrival,
  partialHours,
  lastBookingDate,
}) => {
  const { adjustedStayDate, adjustedBookingDate } = calculateComparisonDates(
    targetDate,
    bookingDate,
    alignmentToggle,
    symmetricComparisonToggle,
    dateComparison,
    { startDate, endDate },
    resetBookingDate,
    partialHours,
    lastBookingDate
  );
  const hotelIds = !hotels?.hotels
    ? [hotels?.hotelCode]
    : hotels?.hotels?.map((hotel) => hotel?.hotelCode);
  return {
    // stay date filters
    ...createCeruleanDateFilter(DYNAMIC_STAY_DATE_FILTER_LOWER, targetDate.startDate),
    ...createCeruleanDateFilter(DYNAMIC_STAY_DATE_FILTER_UPPER, targetDate.endDate),

    // comparison stay date filters
    ...createCeruleanDateFilter(
      DYNAMIC_COMPARISON_TIME_FILTER_STAY_DATE_LOWER,
      adjustedStayDate?.startDate
    ),
    ...createCeruleanDateFilter(
      DYNAMIC_COMPARISON_TIME_FILTER_STAY_DATE_UPPER,
      adjustedStayDate?.endDate
    ),

    // booking date filters
    ...createCeruleanDateFilter(BOOKING_DATE_FILTER_LOWER, bookingDate.startDate),
    ...createCeruleanDateFilter(BOOKING_DATE_FILTER_UPPER, bookingDate.endDate),

    // comparison booking date filters
    ...createCeruleanDateFilter(
      COMPARISON_BOOKING_DATE_FILTER_LOWER,
      adjustedBookingDate?.startDate
    ),
    ...createCeruleanDateFilter(COMPARISON_BOOKING_DATE_FILTER_UPPER, adjustedBookingDate?.endDate),

    // PMS filter
    ...createCeruleanFilter(PMS_FILTER, pms?.toString()),

    // hotel filter
    ...createCeruleanFilter(HOTEL_FILTER, hotelIds),

    // currency filter
    ...createCeruleanFilter(CURRENCY_FILTER, currencyType),

    // selected date filter
    ...createCeruleanFilter(ARRIVAL_STAY_DATE_SWITCH_FILTER, arrival),
  };
};

export const buildConsortiaFilters = ({
  bookingDate,
  targetDate,
  pms,
  hotels,
  currencyType,
  alignmentToggle,
  symmetricComparisonToggle,
  dateComparison,
  startDate,
  endDate,
  resetBookingDate,
  partialHours,
  lastBookingDate,
  selectedDateType,
}) => {
  const { adjustedStayDate, adjustedBookingDate } = calculateComparisonDates(
    targetDate,
    bookingDate,
    alignmentToggle,
    symmetricComparisonToggle,
    dateComparison,
    { startDate, endDate },
    resetBookingDate,
    partialHours,
    lastBookingDate
  );

  const hotelIds = !hotels?.hotels
    ? [hotels?.hotelCode]
    : hotels?.hotels?.map((hotel) => hotel?.hotelCode);
  return {
    // stay date filters
    ...createCeruleanDateFilter(DYNAMIC_STAY_DATE_FILTER_LOWER, targetDate.startDate),
    ...createCeruleanDateFilter(DYNAMIC_STAY_DATE_FILTER_UPPER, targetDate.endDate),

    // comparison stay date filters
    ...createCeruleanDateFilter(
      DYNAMIC_COMPARISON_TIME_FILTER_STAY_DATE_LOWER,
      adjustedStayDate?.startDate
    ),
    ...createCeruleanDateFilter(
      DYNAMIC_COMPARISON_TIME_FILTER_STAY_DATE_UPPER,
      adjustedStayDate?.endDate
    ),

    // booking date filters
    ...createCeruleanDateFilter(BOOKING_DATE_FILTER_LOWER, bookingDate.startDate),
    ...createCeruleanDateFilter(BOOKING_DATE_FILTER_UPPER, bookingDate.endDate),

    // comparison booking date filters
    ...createCeruleanDateFilter(
      COMPARISON_BOOKING_DATE_FILTER_LOWER,
      adjustedBookingDate?.startDate
    ),
    ...createCeruleanDateFilter(COMPARISON_BOOKING_DATE_FILTER_UPPER, adjustedBookingDate?.endDate),

    // PMS filter
    ...createCeruleanFilter(PMS_FILTER, pms?.toString()),

    // hotel filter
    ...createCeruleanFilter(HOTEL_FILTER, hotelIds),

    // currency filter
    ...createCeruleanFilter(CURRENCY_FILTER, currencyType),
    // selected date filter
    ...createCeruleanFilter(
      ARRIVAL_STAY_DATE_SWITCH_FILTER,
      selectedDateType === 'ARRIVAL' ? 'arrival_date' : 'stay_date'
    ),
  };
};

export const buildConsortiaHeaderData = ({
  targetDate,
  bookingDate,
  alignmentToggle,
  symmetricComparisonToggle,
  currencyType,
  aligned = false,
  selectedDateType,
  dateComparison,
  startDate,
  endDate,
  resetBookingDate,
  partialHours,
  lastBookingDate,
}) => {
  const isOtb =
    isSameDay(new Date(bookingDate?.startDate), new Date(resetBookingDate?.startDate)) &&
    isSameDay(new Date(bookingDate?.endDate), new Date(resetBookingDate?.endDate));

  const { adjustedStayDate, adjustedBookingDate } = calculateComparisonDates(
    targetDate,
    bookingDate,
    alignmentToggle,
    symmetricComparisonToggle,
    dateComparison,
    { startDate, endDate },
    resetBookingDate,
    partialHours,
    lastBookingDate
  );
  //
  const stayDateRange = `${format(
    new Date(targetDate.startDate),
    YEAR_MONTH_DAY_PATTERN
  )} to ${format(new Date(targetDate.endDate), YEAR_MONTH_DAY_PATTERN)}`;

  const stayDateComparison = `${format(
    new Date(adjustedStayDate.startDate),
    YEAR_MONTH_DAY_PATTERN
  )} to ${format(new Date(adjustedStayDate.endDate), YEAR_MONTH_DAY_PATTERN)}`;
  //
  const bookingDateRange = `${format(
    new Date(bookingDate.startDate),
    YEAR_MONTH_DAY_PATTERN
  )} to ${format(new Date(bookingDate.endDate), YEAR_MONTH_DAY_PATTERN)}`;
  const bookingDateComparison = `${format(
    new Date(adjustedBookingDate.startDate),
    YEAR_MONTH_DAY_PATTERN
  )} to ${format(new Date(adjustedBookingDate.endDate), YEAR_MONTH_DAY_PATTERN)}`;
  //
  const comparisonLabel = getDateComparison(dateComparison, aligned);
  return [
    {
      id: 1,
      title: selectedDateType === 'ARRIVAL' ? ARRIVAL_DATE_RANGE : STAY_DATE_RANGE,
      value: stayDateRange,
    },
    { id: 2, title: BOOKING_DATE_RANGE, value: isOtb ? 'OTB' : bookingDateRange },
    { title: SELECTED_CURRENCY, value: currencyType },
    { id: 3, title: STAY_DATE_COMPARISON, value: stayDateComparison },
    {
      id: 4,
      title: BOOKING_DATE_COMPARISON,
      value: `${bookingDateComparison} ${symmetricComparisonToggle ? ' (STLP)' : ''}`,
    },
    { id: 5, title: CURRENCY_COMPARISON, value: comparisonLabel },
  ];
};

export const buildPacePlanHeaderData = ({
  bookingDate,
  targetDate,
  pms,
  hotels,
  selectedDateType,
  resetBookingDate,
}) => {
  const isOtb =
    isSameDay(new Date(bookingDate?.startDate), new Date(resetBookingDate?.startDate)) &&
    isSameDay(new Date(bookingDate?.endDate), new Date(resetBookingDate?.endDate));

  const stayStartDate = new Date(targetDate.startDate);
  const stayDateRange = `${format(startOfMonth(stayStartDate), YEAR_MONTH_DAY_PATTERN)} to ${format(
    lastDayOfMonth(addMonths(stayStartDate, 11)),
    YEAR_MONTH_DAY_PATTERN
  )}`;
  const bookingDateRange = `${format(
    new Date(bookingDate.startDate),
    YEAR_MONTH_DAY_PATTERN
  )} to ${format(new Date(bookingDate.endDate), YEAR_MONTH_DAY_PATTERN)}`;

  return {
    title: PACE_TABULAR_TITLE,
    hotelName: hotels?.label,
    sections: [
      {
        id: 1,
        title:
          selectedDateType?.id === VISUAL_DATE_TYPES.ARRIVAL ? ARRIVAL_DATE_RANGE : STAY_DATE_RANGE,
        value: stayDateRange,
      },
      { id: 2, title: BOOKING_DATE_RANGE, value: isOtb ? 'OTB' : bookingDateRange },
      { title: PMS_SYNC, value: pms ? 'On' : 'Off' },
    ],
  };
};

export const buildNegConIataFilters = ({
  selectedDate,
  hotels,
  currencyType,
  currentYear,
  isDemoMode,
}) => {
  const hotelIds = !hotels?.hotels
    ? [hotels?.hotelCode]
    : hotels?.hotels?.map((hotel) => hotel?.hotelCode);
  return {
    // hotel filter
    ...createCeruleanFilter(HOTEL_FILTER, hotelIds),

    // currency filter
    ...createCeruleanFilter(CURRENCY_FILTER, currencyType),

    // Selected date filter
    ...createCeruleanFilter(SELECTED_DATE_FILTER, selectedDate),

    // last year filter
    ...createCeruleanFilter(
      LAST_YEAR_FILTER,
      currentYear - 1,
      TABLE_MEASURES,
      COLUMN_DYNAMIC_MEASURE_ST
    ),

    // current year filter
    ...createCeruleanFilter(
      CURRENT_YEAR_FILTER,
      currentYear,
      TABLE_MEASURES,
      COLUMN_DYNAMIC_MEASURE_ST
    ),

    // year before last year filter
    ...createCeruleanFilter(
      YEAR_BEFORE_LAST_YEAR_FILTER,
      currentYear - 2,
      TABLE_MEASURES,
      COLUMN_DYNAMIC_MEASURE_ST
    ),

    // PreDefined filter
    ...createCeruleanFilter(IS_PREDEFINED, hotels?.isPredefined ?? false),

    // Anonymized filter
    ...createCeruleanFilter(ANONYMIZED, isDemoMode),

    // Set Name filter
    ...createCeruleanFilter(SET_NAME, 'hotels' in hotels ? hotels?.label : 'Default'),
  };
};
// Utility function to extract and format filter values for display in the UI
export const getHeaderFilterValues = (
  filters,
  resetBookingDate,
  dateComparison,
  align,
  selectedDateType,
  symmetricComparison
) => {
  const result = [];

  const getRange = (conditions = []) => {
    const lower = conditions.find((c) => c.operator === 'GreaterThanOrEqual')?.value;
    const upper = conditions.find((c) => c.operator === 'LessThanOrEqual')?.value;
    if (!lower || !upper) {
      return '';
    }
    return `${formatDate(lower, YEAR_MONTH_DAY_PATTERN)} - ${formatDate(
      upper,
      YEAR_MONTH_DAY_PATTERN
    )}`;
  };

  const mappings = {
    Dim_SDate: 'Stay Date Selection',
    Dim_ADate: 'Arrival Date Selection',
    slicerdates:
      selectedDateType === 'ARRIVAL' ? 'Arrival Date Comparison' : 'Stay Date Comparison',
    Dim_BDate: 'Booking Date Selection',
    pms_switch: 'PMS Sync',
    slicerBdates: 'Booking Date Comparison',
    dateComparison: 'Comparison',
  };
  //
  const dateComparisonMap = {
    [compareWindows.otb]: 'OTB ',
    [compareWindows.py]: 'STLY ',
    [compareWindows.pm]: 'STLM ',
    [compareWindows.pw]: 'STLW ',
    [compareWindows.cus]: '',
  };

  // required order
  const orderedTables = [
    'Dim_SDate',
    'Dim_ADate',
    'slicerdates',
    'Dim_BDate',
    'slicerBdates',
    'pms_switch',
    'dateComparison',
  ];

  const groupedFilters = Object.fromEntries(
    filters.map((filter) => [filter?.target?.table, filter])
  );

  orderedTables.forEach((table) => {
    const filter = groupedFilters[table];

    if (!filter) {
      return;
    }

    // PMS Sync
    if (table === 'pms_switch') {
      result.push(`${mappings[table]}: ${filter?.values?.[0] ?? 'On'}`);

      return;
    }
    if (table === 'Dim_BDate') {
      const lower = filter.conditions?.find((c) => c.operator === 'GreaterThanOrEqual')?.value;
      const upper = filter.conditions?.find((c) => c.operator === 'LessThanOrEqual')?.value;

      const isOtb =
        isSameDay(new Date(lower), new Date(resetBookingDate?.startDate)) &&
        isSameDay(new Date(upper), new Date(resetBookingDate?.endDate));
      //
      result.push(
        `${mappings[table]}: ${
          isOtb
            ? `OTB (to ${formatDate(resetBookingDate.endDate, YEAR_MONTH_DAY_PATTERN)})`
            : getRange(filter?.conditions)
        }`
      );
      //
      if (symmetricComparison) {
        const range = groupedFilters.slicerBdates?.conditions;
        //
        result.push(
          `${mappings.slicerBdates}: ${
            isOtb
              ? `${dateComparisonMap[dateComparison]}(${getRange(range)})`
              : `${dateComparisonMap[dateComparison]}(${getRange(range)})`
          }`
        );
      } else {
        result.push(
          `${mappings.slicerBdates}: ${
            isOtb
              ? `OTB (to ${formatDate(resetBookingDate.endDate, YEAR_MONTH_DAY_PATTERN)})`
              : `OTB (to ${formatDate(upper, YEAR_MONTH_DAY_PATTERN)})`
          }`
        );
      }

      return;
    }

    if (table === 'slicerBdates') {
      return;
    }
    // Date range filters
    result.push(`${mappings[table]}: ${getRange(filter?.conditions)}`);
  });
  result.push(`${mappings.dateComparison}: ${getDateComparison(dateComparison, align)}`);
  return result;
};
